/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Util" />
import { IIterationData, ITeam, ITeamSettings } from "Agile/Contracts/Common";
import * as React from "react";
import { IReadonlyObservableArray, ObservableCollection } from "VSS/Core/Observable";
import { getMenuItems, IExternalMenuItem } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IClientWorkItem, IObservableClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IPlanningPaneViewService } from "Wit/Common/PlanningPane/Services/PlanningPaneViewService";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { DeleteDialog } from "Wit/Common/WorkItemDialogs/Components/DeleteDialog";
import { IWorkItemFormDialogService } from "Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService";
import { ITaskboardWorkItem } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ITaskboardService } from "Wit/SprintsHub/SprintsView/Services/ITaskboardService";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

export function getDefaultMenuItems(
    pageContext: IVssPageContext,
    onCardClick: (workItemId: number) => void,
    teamSettings: ITeamSettings | undefined,
    currentItem: IObservableClientWorkItem<ITaskboardWorkItem>,
    hasPermissionToDeleteWorkItem: boolean
): ObservableCollection<IMenuItem> {
    const updateIterations = async (index: number, iteration: IIterationData) => {
        const planningPaneSvc = pageContext.getService<IPlanningPaneViewService>("IPlanningPaneViewService");
        if (!planningPaneSvc.planningItemProvider && teamSettings) {
            const items = [];
            if (teamSettings.teamName) {
                items.push(teamSettings.teamName);
            }
            if (teamSettings.currentIteration) {
                items.push(teamSettings.currentIteration);
            }
            if (teamSettings.futureIterations && teamSettings.futureIterations?.length) {
                items.push(...teamSettings.futureIterations);
            }
            planningPaneSvc.planningItemProvider = new ArrayItemProvider(items);
        }

        const useChangeIterationPerfomanceOptimization = isFeatureFlagEnabled(
            pageContext,
            FeatureFlags.UseChangeIterationPerfomanceOptimization,
            false
        );

        const currentIterationData = planningPaneSvc.getIterationInfo(index);
        const taskboardService = pageContext.getService<ITaskboardService>("ITaskboardService");
        await taskboardService.updateIteration(
            currentIterationData, 
            currentItem.value, 
            async (workItemDTO, numberOfSavedWorkitems) => {
                planningPaneSvc.updateIterationPanel(index, workItemDTO, numberOfSavedWorkitems);
            },
            useChangeIterationPerfomanceOptimization);
    };
    const iterationItems = WorkItemUtils.setIterationItems(teamSettings, updateIterations);
    const menuItems = new ObservableCollection<IMenuItem>();
    menuItems.push([
        {
            id: "open",
            groupKey: "open-work-item",
            text: Resources.Open,
            rank: 10,
            iconProps: { ariaHidden: true, iconName: "ReplyMirrored" },
            onActivate: () => {
                onCardClick(currentItem.value.id);
            }
        },
        { id: "divider", itemType: MenuItemType.Divider },
        {
            id: "edit-title",
            groupKey: "manage-work-item",
            text: Resources.EditTitle,
            rank: 20,
            iconProps: { ariaHidden: true, iconName: "Edit" },
            onActivate: () => {
                currentItem.value.isTitleEditing.value = true;
            }
        },
        {
            id: "move-to-iteration",
            rank: 30,
            groupKey: "manage-work-item",
            text: Resources.MoveToIteration,
            subMenuProps: { id: "iteration-sub-menu-items", items: iterationItems }
        }
    ]);
    hasPermissionToDeleteWorkItem &&
        menuItems.push([
            {
                id: "delete",
                groupKey: "manage-work-item",
                text: Resources.Delete,
                rank: 40,
                iconProps: { ariaHidden: true, iconName: "Delete" },
                onActivate: () => {
                    getDeleteDialog(pageContext, currentItem.value.id, currentItem.value.getFieldValue(CoreFieldRefNames.TeamProject));
                }
            }
        ]);
    menuItems.push([
        { id: "divider", itemType: MenuItemType.Divider },
        WorkItemUtils.getNewBranchCommandMenuItem(pageContext, (): INamedFieldDataDictionary[] => {
            return [currentItem.value.getFields()];
        })
    ]);
    return menuItems;
}

export function getDeleteDialog(pageContext: IVssPageContext, workItemId: number, projectName: string | undefined | null): void {
    openDialog(pageContext, onDismiss => (
        <DeleteDialog
            onDismiss={onDismiss}
            selectedItems={[
                {
                    [CoreFieldRefNames.Id]: workItemId,
                    [CoreFieldRefNames.TeamProject]: projectName
                }
            ]}
            onDeleteItems={() => {
                removeItem(pageContext, workItemId);
            }}
        />
    ));
}

async function removeItem(pageContext: IVssPageContext, itemId: number): Promise<void> {
    const workItemService: ITaskboardService = pageContext.getService<ITaskboardService>("ITaskboardService");

    await workItemService.removeWorkItem(itemId, { processChildItems: true });
}

function openDialog(pageContext: IVssPageContext, callout: (onDismiss: () => void) => JSX.Element): void {
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => callout(onDismiss));
}

const TeamContextDataProvider = "ms.vss-tfs-web.team-context-data-provider";
interface IContributedItemContext {
    id?: number;
    workItemType?: string;
    team?: ITeam;
}

export function addContributedMenuItems(
    pageContext: IVssPageContext,
    menuItems: ObservableCollection<IMenuItem>,
    workItemId: number,
    workItemType: string
): IReadonlyObservableArray<IMenuItem> {
    // contributed items
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const currentTeam = contributionService.getData<ITeam | undefined>(TeamContextDataProvider);

    const context: IContributedItemContext = {
        team: currentTeam,
        id: workItemId,
        workItemType: workItemType
    };

    const processExternalMenuItem = (menuItem: IMenuItem, contribution: IMenuItem | IExternalMenuItem) => {
        return menuItems.value.find(item => item.id === menuItem.id) ? undefined : menuItem;
    };

    menuItems.push(getMenuItems(pageContext, "ms.vss-work-web.work-item-context-menu-owp", context, { processExternalMenuItem }, true));
    menuItems.push(getMenuItems(pageContext, "ms.vss-work-web.work-item-context-menu", context, { processExternalMenuItem }, true));

    return menuItems;
}

export function openWorkItemFormDialog(pageContext: IVssPageContext, itemOrItemId: number | IClientWorkItem) {
    const taskboardService = pageContext.getService<ITaskboardService>("ITaskboardService");
    let itemId: number;
    let item: IClientWorkItem | undefined;
    if (typeof itemOrItemId === "number") {
        itemId = itemOrItemId;
        item = taskboardService.get(itemId)?.value;
    } else {
        item = itemOrItemId;
        itemId = item.id;
    }
    const workItemFormDialogService = pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");
    workItemFormDialogService.showWorkItemFormDialog(item?.iWorkItem.value ?? itemId, onWorkItemFormSaveComplete, {
        updateHistory: true,
        onDelete: async () => {
            await taskboardService.removeWorkItem(itemId, { processChildItems: true });
        }
    });
}

export async function onWorkItemFormSaveComplete(pageContext: IVssPageContext, workItem: IWorkItem, includeHistory?: boolean) {
    const itemId = workItem.id;
    const taskboardService = pageContext.getService<ITaskboardService>("ITaskboardService");
    const item = taskboardService.get(workItem);
    if (!item) {
        return;
    }

    const originalParentId: number | undefined = item.value.getFields()[CoreFieldRefNames.Parent];
    // Update the item
    await item.refresh(pageContext, includeHistory);

    // did this item's parent change?
    item.updateParentTitle(pageContext, originalParentId, id => taskboardService.get(id));

    // is this item the parent of any children, and if so, do we need to update their parent titles?
    const itemTitle = item.value.getRequiredFieldValue<string>(CoreFieldRefNames.Title);
    taskboardService.updateParentFieldTitle(itemId, itemTitle);

    item.notify();
}
